import * as React from "react";
import {
  Archive,
  ArrowLeft,
  BookOpen,
  ClipboardCheck,
  Check,
  ChevronRight,
  Code2,
  Download,
  Eraser,
  FileImage,
  Gamepad2,
  GraduationCap,
  Home,
  ImagePlus,
  LayoutDashboard,
  Link,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  PaintBucket,
  Palette,
  Pencil,
  Play,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trophy,
  Unlock,
  Upload,
  UserPlus,
  Users,
  Video,
} from "lucide-react";
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getBlob, ref, uploadBytes } from "firebase/storage";
import { adminEmail, auth, db, firebaseReady, storage } from "./firebase";
import { DEFAULT_UNITS, mergeCourseUnits } from "./curriculum";
import { gradeProject, parseGameValues } from "./lib/autograde";
import { downloadProject } from "./lib/project-download";
import { PythonStudio } from "./PythonStudio";
import type {
  AssetKind,
  AssetRecord,
  AutoGrade,
  ClassSettingsRecord,
  CourseUnit,
  InviteRecord,
  ProjectAssetState,
  ProjectRecord,
  Role,
  TeacherGuideRecord,
  UnitResource,
  Viewer,
} from "./types";

const SCHOOL_YEAR = "2026-2027";
const PROJECT_ID_SUFFIX = "catch-star";
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const PAINT_GRID_SIZE = 16;
const TRANSPARENT = "transparent";
const PAINT_COLORS = ["#14213a", "#6657e8", "#2f8cff", "#34c98f", "#8cffc9", "#ffd45c", "#ff8a5b", "#f54b78", "#ffffff"];

type PaintTool = "pencil" | "eraser" | "fill";
type PaintDrafts = Record<AssetKind, string[]>;

function createPaintDrafts(): PaintDrafts {
  return {
    hero: Array.from({ length: PAINT_GRID_SIZE * PAINT_GRID_SIZE }, () => TRANSPARENT),
    background: Array.from({ length: PAINT_GRID_SIZE * PAINT_GRID_SIZE }, () => "#14213a"),
    collectible: Array.from({ length: PAINT_GRID_SIZE * PAINT_GRID_SIZE }, () => TRANSPARENT),
  };
}

const RETIRED_STARTER_MARKER = "# Catch the Star — guided Python game";

type View = "course" | "workspace" | "review" | "guide" | "media" | "builder" | "accounts";

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

function projectIdFor(uid: string) {
  return `${uid}-${PROJECT_ID_SUFFIX}`;
}

function classIdFor(className: string) {
  return encodeURIComponent(className.trim().toLowerCase()).replaceAll("%", "-");
}

function emptyResource(): UnitResource {
  return { slidesUrl: "", videoUrl: "", teacherNote: "" };
}

function youtubeEmbedUrl(url: string) {
  if (!url.trim()) return "";
  try {
    const parsed = new URL(url);
    const id = parsed.hostname.includes("youtu.be")
      ? parsed.pathname.slice(1).split("/")[0]
      : parsed.searchParams.get("v") || (parsed.pathname.includes("/embed/") ? parsed.pathname.split("/embed/")[1]?.split("/")[0] : "");
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : "";
  } catch {
    return "";
  }
}

function emptyTeacherGuide(unit: CourseUnit): TeacherGuideRecord {
  return { id: unit.id, week: unit.week, teacherPlan: [], demoCode: "", rubric: [] };
}

async function loadCourseUnits(): Promise<CourseUnit[]> {
  if (!db) return DEFAULT_UNITS;
  const snapshot = await getDocs(collection(db, "courseUnits"));
  return mergeCourseUnits(snapshot.docs.map((item) => item.data() as CourseUnit));
}

async function loadTeacherGuides(): Promise<TeacherGuideRecord[]> {
  if (!db) return [];
  const snapshot = await getDocs(collection(db, "teacherGuides"));
  return snapshot.docs.map((item) => item.data() as TeacherGuideRecord).sort((a, b) => a.week - b.week);
}

async function loadClassSettings(className: string): Promise<ClassSettingsRecord> {
  const fallback: ClassSettingsRecord = { className, unlockedUnits: [1], resources: {} };
  if (!db || className === "All classes") return fallback;
  const snapshot = await getDoc(doc(db, "classSettings", classIdFor(className)));
  if (!snapshot.exists()) return fallback;
  const data = snapshot.data() as ClassSettingsRecord;
  return {
    className,
    unlockedUnits: Array.from(new Set([1, ...(data.unlockedUnits || [])])).sort((a, b) => a - b),
    resources: data.resources || {},
  };
}

function toViewer(uid: string, data: Record<string, unknown>): Viewer {
  return {
    uid,
    email: String(data.email || "").toLowerCase(),
    displayName: String(data.displayName || data.email || "Student"),
    role: (data.role as Role) || "student",
    className: String(data.className || "CST 121"),
    active: data.active !== false,
  };
}

async function resolveViewer(user: User): Promise<Viewer | null> {
  if (!db || !user.email) return null;
  const email = user.email.toLowerCase();
  const userRef = doc(db, "users", user.uid);

  if (email === adminEmail) {
    const profile: Viewer = {
      uid: user.uid,
      email,
      displayName: user.displayName || "Albert Paolucci",
      role: "admin",
      className: "All classes",
      active: true,
    };
    await setDoc(userRef, { ...profile, updatedAt: serverTimestamp() }, { merge: true });
    return profile;
  }

  const userSnapshot = await getDoc(userRef);
  if (userSnapshot.exists()) return toViewer(user.uid, userSnapshot.data());

  const inviteSnapshot = await getDoc(doc(db, "invites", email));
  if (!inviteSnapshot.exists()) return null;
  const invite = inviteSnapshot.data() as InviteRecord;
  if (!invite.active) return null;

  const profile: Viewer = {
    uid: user.uid,
    email,
    displayName: invite.displayName || user.displayName || email,
    role: invite.role,
    className: invite.className,
    active: true,
  };
  await setDoc(userRef, { ...profile, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  return profile;
}

export default function App() {
  const [viewer, setViewer] = React.useState<Viewer | null>(null);
  const [checking, setChecking] = React.useState(firebaseReady);
  const [pendingEmail, setPendingEmail] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, async (user) => {
      setChecking(true);
      setError("");
      if (!user) {
        setViewer(null);
        setPendingEmail("");
        setChecking(false);
        return;
      }
      try {
        const profile = await resolveViewer(user);
        setViewer(profile?.active ? profile : null);
        setPendingEmail(profile ? "" : user.email || "your Google account");
      } catch (problem) {
        setError(problem instanceof Error ? problem.message : "Your account could not be checked.");
      } finally {
        setChecking(false);
      }
    });
  }, []);

  async function login() {
    if (!auth) return;
    setError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Google sign-in did not finish.");
    }
  }

  if (!firebaseReady) return <SetupScreen />;
  if (checking) return <LoadingScreen />;
  if (pendingEmail) {
    return (
      <AccessScreen
        email={pendingEmail}
        onSignOut={() => auth && signOut(auth)}
      />
    );
  }
  if (!viewer) return <SignInScreen onSignIn={login} error={error} />;

  return <GameLab viewer={viewer} onSignOut={() => auth && signOut(auth)} />;
}

function SetupScreen() {
  return (
    <main className="center-page">
      <section className="auth-card">
        <div className="auth-brand">
          <Gamepad2 size={34} />
          <p className="eyebrow">Paolucci Studio</p>
          <h1>Python Game Lab</h1>
          <p>The permanent Firebase application is ready to be connected.</p>
        </div>
        <div className="auth-body">
          <div className="notice">
            <ShieldCheck />
            <p>Firebase settings are not installed yet. Add the web-app configuration before publishing.</p>
          </div>
        </div>
      </section>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="center-page">
      <div className="loading-box"><LoaderCircle className="spin" /> Checking your private workspace…</div>
    </main>
  );
}

function SignInScreen({ onSignIn, error }: { onSignIn: () => void; error: string }) {
  return (
    <main className="center-page">
      <section className="auth-card">
        <div className="auth-brand">
          <Gamepad2 size={34} />
          <p className="eyebrow">Paolucci Studio</p>
          <h1>Python Game Lab</h1>
          <p>Learn Python, upload original game art, and keep every project private.</p>
        </div>
        <div className="auth-body">
          <div className="notice"><LockKeyhole /><p><strong>Approved Google account required.</strong> Students only see their own work.</p></div>
          <button className="button google-button" onClick={onSignIn}>Continue with Google</button>
          {error ? <p className="error-box">{error}</p> : null}
          <p className="small-center"><ShieldCheck /> Google verifies identity. Game Lab controls access.</p>
        </div>
      </section>
    </main>
  );
}

function AccessScreen({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <main className="center-page">
      <section className="message-card">
        <LockKeyhole size={34} />
        <h1>Teacher approval needed</h1>
        <p><strong>{email}</strong> is signed in, but it has not been assigned to a class.</p>
        <p>Ask your teacher to approve this exact Google email.</p>
        <button className="button secondary" onClick={onSignOut}>Use a different account</button>
      </section>
    </main>
  );
}

function GameLab({ viewer, onSignOut }: { viewer: Viewer; onSignOut: () => void }) {
  const [view, setView] = React.useState<View>(viewer.role === "student" ? "course" : "review");
  const [selectedUnit, setSelectedUnit] = React.useState<CourseUnit>(DEFAULT_UNITS[0]);
  function openUnit(unit: CourseUnit) {
    setSelectedUnit(unit);
    setView("workspace");
  }
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="logo-lockup"><span><Gamepad2 /></span><div><strong>PAOLUCCI STUDIO</strong><small>Python Game Lab</small></div></div>
        <nav aria-label="Main navigation">
          <p className="nav-label">LEARN · CODE · PLAY</p>
          <NavButton active={view === "course"} onClick={() => setView("course")} icon={<Home />}>Course map</NavButton>
          <NavButton active={view === "workspace"} onClick={() => setView("workspace")} icon={<Gamepad2 />}>Game workshop</NavButton>
          {viewer.role !== "student" ? <NavButton active={view === "review"} onClick={() => setView("review")} icon={<Users />}>Teacher dashboard</NavButton> : null}
          {viewer.role !== "student" ? <NavButton active={view === "guide"} onClick={() => setView("guide")} icon={<BookOpen />}>Teacher guide</NavButton> : null}
          {viewer.role !== "student" ? <NavButton active={view === "media"} onClick={() => setView("media")} icon={<Video />}>Videos & slides</NavButton> : null}
          {viewer.role === "admin" ? <NavButton active={view === "builder"} onClick={() => setView("builder")} icon={<SlidersHorizontal />}>Course builder</NavButton> : null}
          {viewer.role === "admin" ? <NavButton active={view === "accounts"} onClick={() => setView("accounts")} icon={<UserPlus />}>Manage accounts</NavButton> : null}
        </nav>
        <div className="sidebar-profile">
          <span>{viewer.displayName.slice(0, 1).toUpperCase()}</span>
          <div><strong>{viewer.displayName}</strong><small>{viewer.role} · {viewer.email}</small></div>
          <button onClick={onSignOut} aria-label="Sign out"><LogOut /></button>
        </div>
      </aside>
      <main className="main-content">
        <header className="mobile-header"><div className="logo-lockup"><span><Gamepad2 /></span><strong>Python Game Lab</strong></div><button onClick={onSignOut}><LogOut /></button></header>
        <nav className="mobile-nav" aria-label="Mobile navigation">
          <NavButton active={view === "course"} onClick={() => setView("course")} icon={<Home />}>Course</NavButton>
          <NavButton active={view === "workspace"} onClick={() => setView("workspace")} icon={<Gamepad2 />}>Workshop</NavButton>
          {viewer.role !== "student" ? <NavButton active={view === "review"} onClick={() => setView("review")} icon={<Users />}>Dashboard</NavButton> : null}
          {viewer.role !== "student" ? <NavButton active={view === "guide"} onClick={() => setView("guide")} icon={<BookOpen />}>Guide</NavButton> : null}
          {viewer.role !== "student" ? <NavButton active={view === "media"} onClick={() => setView("media")} icon={<Video />}>Media</NavButton> : null}
          {viewer.role === "admin" ? <NavButton active={view === "builder"} onClick={() => setView("builder")} icon={<SlidersHorizontal />}>Builder</NavButton> : null}
          {viewer.role === "admin" ? <NavButton active={view === "accounts"} onClick={() => setView("accounts")} icon={<UserPlus />}>Accounts</NavButton> : null}
        </nav>
        {view === "course" ? <CourseMap viewer={viewer} onLaunch={openUnit} /> : null}
        {view === "workspace" ? <MissionWorkspace viewer={viewer} unit={selectedUnit} onBack={() => setView("course")} /> : null}
        {view === "review" ? <TeacherDashboard viewer={viewer} /> : null}
        {view === "guide" ? <TeacherGuide viewer={viewer} /> : null}
        {view === "media" ? <TeacherMediaLibrary viewer={viewer} /> : null}
        {view === "builder" && viewer.role === "admin" ? <CourseBuilder /> : null}
        {view === "accounts" ? <AccountManagement /> : null}
      </main>
    </div>
  );
}

function NavButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return <button className={classNames("nav-button", active && "active")} onClick={onClick}>{icon}<span>{children}</span></button>;
}

function CourseMap({ viewer, onLaunch }: { viewer: Viewer; onLaunch: (unit: CourseUnit) => void }) {
  const [units, setUnits] = React.useState<CourseUnit[]>(DEFAULT_UNITS);
  const [unlocked, setUnlocked] = React.useState<number[]>([1]);
  const [completed, setCompleted] = React.useState<number[]>([]);
  const [message, setMessage] = React.useState("Loading your course…");
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      const [course, settings, project] = await Promise.all([
        loadCourseUnits(),
        loadClassSettings(viewer.className),
        db ? getDoc(doc(db, "projects", projectIdFor(viewer.uid))) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      setUnits(course.filter((unit) => unit.published || viewer.role === "admin"));
      setUnlocked(viewer.role === "student" ? settings.unlockedUnits : course.map((unit) => unit.week));
      if (project && "exists" in project && project.exists()) {
        setCompleted((project.data() as ProjectRecord).submittedUnitIds || []);
      }
      setMessage("");
    }
    void load().catch((problem) => !cancelled && setMessage(problem instanceof Error ? problem.message : "The course could not be loaded."));
    return () => { cancelled = true; };
  }, [viewer]);
  const progress = Math.round((new Set(completed).size / Math.max(1, units.length)) * 100);
  return (
    <div className="page quest-bg">
      <div className="page-heading"><div><p className="pill">15-WEEK PYTHON PLATFORMER</p><h1>Build one game, one unit at a time</h1><p>Every week teaches a Python concept and adds a working feature to the same platform game.</p></div><div className="progress-card"><div><strong>Course progress</strong><strong>{progress}%</strong></div><progress value={progress} max="100" /></div></div>
      {message ? <p className="info-message course-message">{message}</p> : null}
      <section className="unit-grid">
        {units.map((unit) => {
          const locked = viewer.role === "student" && !unlocked.includes(unit.week);
          const done = completed.includes(unit.week);
          return <article className={classNames("unit-card", locked && "locked", done && "done")} key={unit.id}>
            <div className="unit-number"><span>{done ? <Check /> : locked ? <LockKeyhole /> : <Gamepad2 />}</span><strong>UNIT {unit.week}</strong></div>
            <h2>{unit.title}</h2>
            <p>{unit.summary}</p>
            <div className="unit-meta"><span><Code2 /> {unit.pythonFocus}</span><span><LayoutDashboard /> {unit.gameFeature}</span></div>
            <button className={classNames("button", locked ? "secondary" : "primary", "full")} disabled={locked} onClick={() => onLaunch(unit)}>{locked ? "Teacher unlock required" : done ? "Continue unit" : unit.week === 1 ? "Start Unit 1" : "Open unit"}{!locked ? <ChevronRight /> : null}</button>
          </article>;
        })}
      </section>
    </div>
  );
}

function MissionWorkspace({ viewer, unit, onBack }: { viewer: Viewer; unit: CourseUnit; onBack: () => void }) {
  const [tab, setTab] = React.useState<"learn" | "assets" | "code">("learn");
  const [title, setTitle] = React.useState("My Platform Game");
  const [code, setCode] = React.useState("");
  const [assets, setAssets] = React.useState<ProjectAssetState>({});
  const [paintDrafts, setPaintDrafts] = React.useState<PaintDrafts>(() => createPaintDrafts());
  const [grade, setGrade] = React.useState<AutoGrade>(() => gradeProject("", {}));
  const [status, setStatus] = React.useState<"draft" | "submitted" | "returned">("draft");
  const [submittedUnitIds, setSubmittedUnitIds] = React.useState<number[]>([]);
  const [teacherFeedback, setTeacherFeedback] = React.useState("");
  const [resource, setResource] = React.useState<UnitResource>(() => emptyResource());
  const [message, setMessage] = React.useState("Loading private project…");
  const [busy, setBusy] = React.useState(false);
  const [projectLoaded, setProjectLoaded] = React.useState(false);
  const projectId = projectIdFor(viewer.uid);

  React.useEffect(() => {
    let cancelled = false;
    async function loadProject() {
      if (!db || !storage) return;
      const snapshot = await getDoc(doc(db, "projects", projectId));
      if (!snapshot.exists()) {
        if (!cancelled) {
          setMessage("New private project · autosave is on");
          setProjectLoaded(true);
        }
        return;
      }
      const project = { id: snapshot.id, ...snapshot.data() } as ProjectRecord;
      const loadedAssets: ProjectAssetState = {};
      for (const [kind, asset] of Object.entries(project.assets || {}) as Array<[AssetKind, AssetRecord]>) {
        try {
          const blob = await getBlob(ref(storage, asset.storagePath));
          loadedAssets[kind] = { ...asset, previewUrl: URL.createObjectURL(blob) };
        } catch {
          // Keep loading the rest of the project if one old image is unavailable.
        }
      }
      if (!cancelled) {
        setTitle(project.title || "My Platform Game");
        setCode(project.code?.includes(RETIRED_STARTER_MARKER) ? "" : project.code || "");
        setAssets(loadedAssets);
        setGrade(project.autoGrade || gradeProject(project.code || "", loadedAssets));
        setStatus(project.status || "draft");
        setSubmittedUnitIds(project.submittedUnitIds || []);
        setTeacherFeedback(project.teacherFeedback || "");
        setMessage("Private project restored · autosave is on");
        setProjectLoaded(true);
      }
    }
    loadProject().catch((problem) => !cancelled && setMessage(problem instanceof Error ? problem.message : "Project could not be loaded."));
    return () => { cancelled = true; };
  }, [projectId]);

  React.useEffect(() => {
    if (!projectLoaded || !db || busy) return;
    let cancelled = false;
    setMessage("Unsaved changes…");
    const timer = window.setTimeout(async () => {
      try {
        await setDoc(doc(db!, "projects", projectId), {
          ownerId: viewer.uid,
          ownerEmail: viewer.email,
          ownerName: viewer.displayName,
          className: viewer.className,
          schoolYear: SCHOOL_YEAR,
          title,
          code,
          autoGrade: gradeProject(code, assets),
          status,
          currentUnitId: unit.week,
          submittedUnitIds,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        if (!cancelled) setMessage(`Saved automatically at ${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`);
      } catch (problem) {
        if (!cancelled) setMessage(problem instanceof Error ? `Autosave problem: ${problem.message}` : "Autosave could not finish. Use Save.");
      }
    }, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [assets, busy, code, projectId, projectLoaded, status, submittedUnitIds, title, unit.week, viewer]);

  React.useEffect(() => {
    let cancelled = false;
    void loadClassSettings(viewer.className).then((settings) => {
      if (!cancelled) setResource(settings.resources?.[unit.id] || emptyResource());
    }).catch(() => { if (!cancelled) setResource(emptyResource()); });
    return () => { cancelled = true; };
  }, [unit.id, viewer.className]);

  function chooseImage(kind: AssetKind, file?: File) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessage("Choose a PNG, JPG, WebP, or GIF image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setMessage("The image must be smaller than 2 MB.");
      return;
    }
    setAssets((current) => ({
      ...current,
      [kind]: { name: file.name, mimeType: file.type, storagePath: "", previewUrl: URL.createObjectURL(file), file },
    }));
    setMessage(`${file.name} is ready to save.`);
  }

  async function saveProject(nextStatus = status) {
    if (!db || !storage) return;
    setBusy(true);
    setMessage(nextStatus === "submitted" ? "Submitting project…" : "Saving project…");
    try {
      const storedAssets: Partial<Record<AssetKind, AssetRecord>> = {};
      for (const [kind, asset] of Object.entries(assets) as Array<[AssetKind, NonNullable<ProjectAssetState[AssetKind]>]>) {
        let storagePath = asset.storagePath;
        if (asset.file) {
          const cleanName = asset.file.name.replace(/[^a-z0-9._-]+/gi, "-");
          storagePath = `users/${viewer.uid}/projects/${projectId}/assets/${kind}-${cleanName}`;
          await uploadBytes(ref(storage, storagePath), asset.file, { contentType: asset.file.type });
        }
        if (storagePath) storedAssets[kind] = { name: asset.name, mimeType: asset.mimeType, storagePath };
      }
      const nextGrade = gradeProject(code, assets);
      const nextSubmittedUnits = nextStatus === "submitted"
        ? Array.from(new Set([...submittedUnitIds, unit.week])).sort((a, b) => a - b)
        : submittedUnitIds;
      const record = {
        ownerId: viewer.uid,
        ownerEmail: viewer.email,
        ownerName: viewer.displayName,
        className: viewer.className,
        schoolYear: SCHOOL_YEAR,
        title,
        code,
        assets: storedAssets,
        autoGrade: nextGrade,
        status: nextStatus,
        currentUnitId: unit.week,
        submittedUnitIds: nextSubmittedUnits,
        updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "projects", projectId), record, { merge: true });
      setAssets((current) => Object.fromEntries(Object.entries(current).map(([kind, asset]) => [kind, asset ? { ...asset, storagePath: storedAssets[kind as AssetKind]?.storagePath || asset.storagePath, file: undefined } : asset])) as ProjectAssetState);
      setGrade(nextGrade);
      setStatus(nextStatus);
      setSubmittedUnitIds(nextSubmittedUnits);
      setMessage(nextStatus === "submitted" ? "Submitted to your teacher" : "Saved privately");
    } catch (problem) {
      setMessage(problem instanceof Error ? problem.message : "The project could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function exportProject() {
    setBusy(true);
    setMessage("Preparing project download…");
    try {
      const latestGrade = gradeProject(code, assets);
      await downloadProject({ studentName: viewer.displayName, projectTitle: title, schoolYear: SCHOOL_YEAR, code, assets, grade: latestGrade });
      setGrade(latestGrade);
      setMessage("Project ZIP downloaded");
    } catch {
      setMessage("The project download could not be created.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page workspace-page">
      <header className="workspace-header"><div className="header-title"><button className="icon-button" onClick={onBack} aria-label="Back to course map"><ArrowLeft /></button><div><p>UNIT {unit.week} · {unit.title.toUpperCase()}</p><input aria-label="Project title" value={title} onChange={(event) => setTitle(event.target.value)} /></div></div><div className="header-actions"><span className="save-status">{message}</span><button className="button secondary" disabled={busy} onClick={() => void exportProject()}><Download /> Download ZIP</button><button className="button secondary" disabled={busy} onClick={() => void saveProject()}><Save /> Save</button><button className="button green" disabled={busy} onClick={() => void saveProject("submitted")}><Send /> Submit Unit {unit.week}</button></div></header>
      <div className="tabs" role="tablist" aria-label="Project sections">
        <TabButton active={tab === "learn"} onClick={() => setTab("learn")} icon={<BookOpen />}>Lesson</TabButton>
        <TabButton active={tab === "assets"} onClick={() => setTab("assets")} icon={<Palette />}>Paint & Images</TabButton>
        <TabButton active={tab === "code"} onClick={() => setTab("code")} icon={<Code2 />}>Python IDLE</TabButton>
      </div>
      {tab === "learn" ? <LearnPanel unit={unit} resource={resource} onStart={() => setTab("code")} onPaint={() => setTab("assets")} /> : null}
      {tab === "assets" ? <ImageStudio assets={assets} onChoose={chooseImage} paintDrafts={paintDrafts} setPaintDrafts={setPaintDrafts} /> : null}
      {tab === "code" ? <PythonStudio unit={unit} code={code} setCode={setCode} assets={assets} /> : null}
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return <button role="tab" aria-selected={active} className={classNames("tab-button", active && "active")} onClick={onClick}>{icon}{children}</button>;
}

function LearnPanel({ unit, resource, onStart, onPaint }: { unit: CourseUnit; resource: UnitResource; onStart: () => void; onPaint: () => void }) {
  const videoEmbed = youtubeEmbedUrl(resource.videoUrl);
  return <div className="two-column"><section className="panel learn-panel"><p className="eyebrow purple">WEEK {unit.week} LESSON</p><h2>{unit.title}</h2><p>{unit.summary}</p>{videoEmbed ? <div className="student-video"><iframe src={videoEmbed} title={`Unit ${unit.week} teacher video`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div> : null}<h3>Learning goals</h3><ul className="check-list">{unit.objectives.map((objective) => <li key={objective}><Check />{objective}</li>)}</ul><h3>Build steps</h3><div className="step-grid">{unit.studentSteps.map((step, index) => <div key={step}><span>{index + 1}</span><strong>{step}</strong></div>)}</div><div className="assignment-box"><strong>This week&apos;s platformer checkpoint</strong><p>{unit.assignment}</p></div><div className="lesson-actions"><button className="button primary" onClick={onStart}>Open Python IDLE <ChevronRight /></button><button className="button secondary" onClick={onPaint}><Palette /> Paint a character</button></div></section><aside className="help-stack"><div className="xp-card"><Gamepad2 /><h2>{unit.gameFeature}</h2><p>Your teacher will show the code on the smartboard. Type it into your own file, run it, debug it, and make it yours.</p><div><small>PYTHON FOCUS</small><code>{unit.pythonFocus}</code></div></div><div className="panel"><h3>Vocabulary</h3><div className="tag-list">{unit.vocabulary.map((word) => <span key={word}>{word}</span>)}</div>{resource.slidesUrl || resource.videoUrl ? <div className="resource-links"><strong>Teacher resources</strong>{resource.slidesUrl ? <a href={resource.slidesUrl} target="_blank" rel="noreferrer"><Link /> Open slides</a> : null}{resource.videoUrl ? <a href={resource.videoUrl} target="_blank" rel="noreferrer"><Video /> Watch video</a> : null}</div> : null}</div></aside></div>;
}

const ASSET_LABELS: Record<AssetKind, { title: string; help: string }> = {
  hero: { title: "Player sprite", help: "PNG with a transparent background works best." },
  background: { title: "Game background", help: "Use a wide image for the game world." },
  collectible: { title: "Goal or power-up", help: "Upload a goal, coin, key, or power-up." },
};

function ImageStudio({ assets, onChoose, paintDrafts, setPaintDrafts }: { assets: ProjectAssetState; onChoose: (kind: AssetKind, file?: File) => void; paintDrafts: PaintDrafts; setPaintDrafts: React.Dispatch<React.SetStateAction<PaintDrafts>> }) {
  const [mode, setMode] = React.useState<"paint" | "upload">("paint");
  return <section className="panel"><div className="section-heading"><div><p className="eyebrow purple">PAINT & IMAGE STUDIO</p><h2>Create your platform-game characters</h2><p>Paint pixel art inside Game Lab or upload images made in another program.</p></div><span className="limit-badge">Artwork saves with the project</span></div><div className="asset-code-help"><strong>Use artwork in Python</strong><code>pygame.image.load(&quot;hero.png&quot;)</code><code>pygame.image.load(&quot;background.png&quot;)</code><code>pygame.image.load(&quot;collectible.png&quot;)</code></div><div className="image-mode-tabs"><button className={classNames(mode === "paint" && "active")} onClick={() => setMode("paint")}><Palette /> Paint characters</button><button className={classNames(mode === "upload" && "active")} onClick={() => setMode("upload")}><Upload /> Import images</button></div>{mode === "paint" ? <PixelPainter drafts={paintDrafts} setDrafts={setPaintDrafts} onUseImage={onChoose} /> : <ImageUploader assets={assets} onChoose={onChoose} />}</section>;
}

function ImageUploader({ assets, onChoose }: { assets: ProjectAssetState; onChoose: (kind: AssetKind, file?: File) => void }) {
  return <div className="upload-grid">{(Object.keys(ASSET_LABELS) as AssetKind[]).map((kind) => { const asset = assets[kind]; const label = ASSET_LABELS[kind]; return <label className={classNames("upload-card", asset && "has-image")} key={kind}>{asset ? <img src={asset.previewUrl} alt={`${label.title} preview`} /> : <span className="upload-placeholder"><ImagePlus /><strong>{label.title}</strong></span>}<div><strong>{asset?.name || label.title}</strong><p>{label.help}</p><span className="button secondary"><Upload /> {asset ? "Replace image" : "Choose image"}</span></div><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => onChoose(kind, event.target.files?.[0])} /></label>; })}</div>;
}

function PixelPainter({ drafts, setDrafts, onUseImage }: { drafts: PaintDrafts; setDrafts: React.Dispatch<React.SetStateAction<PaintDrafts>>; onUseImage: (kind: AssetKind, file?: File) => void }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const drawing = React.useRef(false);
  const [kind, setKind] = React.useState<AssetKind>("hero");
  const [tool, setTool] = React.useState<PaintTool>("pencil");
  const [color, setColor] = React.useState("#6657e8");
  const [message, setMessage] = React.useState("Paint a picture, then select Use in game.");

  const redraw = React.useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const cell = canvas.width / PAINT_GRID_SIZE;
    context.clearRect(0, 0, canvas.width, canvas.height);
    for (let y = 0; y < PAINT_GRID_SIZE; y += 1) {
      for (let x = 0; x < PAINT_GRID_SIZE; x += 1) {
        const value = drafts[kind][y * PAINT_GRID_SIZE + x];
        context.fillStyle = value === TRANSPARENT ? ((x + y) % 2 === 0 ? "#ffffff" : "#edf0f5") : value;
        context.fillRect(x * cell, y * cell, cell, cell);
        context.strokeStyle = "rgba(20,33,58,.12)";
        context.strokeRect(x * cell, y * cell, cell, cell);
      }
    }
  }, [drafts, kind]);

  React.useEffect(() => { redraw(); }, [redraw]);

  function floodFill(start: number, target: string, replacement: string, grid: string[]) {
    if (target === replacement) return grid;
    const next = [...grid];
    const open = [start];
    while (open.length) {
      const index = open.pop()!;
      if (next[index] !== target) continue;
      next[index] = replacement;
      const x = index % PAINT_GRID_SIZE;
      const y = Math.floor(index / PAINT_GRID_SIZE);
      if (x > 0) open.push(index - 1);
      if (x < PAINT_GRID_SIZE - 1) open.push(index + 1);
      if (y > 0) open.push(index - PAINT_GRID_SIZE);
      if (y < PAINT_GRID_SIZE - 1) open.push(index + PAINT_GRID_SIZE);
    }
    return next;
  }

  function paint(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rectangle = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(PAINT_GRID_SIZE - 1, Math.floor(((event.clientX - rectangle.left) / rectangle.width) * PAINT_GRID_SIZE)));
    const y = Math.max(0, Math.min(PAINT_GRID_SIZE - 1, Math.floor(((event.clientY - rectangle.top) / rectangle.height) * PAINT_GRID_SIZE)));
    const index = y * PAINT_GRID_SIZE + x;
    setDrafts((current) => {
      const replacement = tool === "eraser" ? TRANSPARENT : color;
      const nextGrid = tool === "fill" ? floodFill(index, current[kind][index], replacement, current[kind]) : current[kind].map((value, pixelIndex) => pixelIndex === index ? replacement : value);
      return { ...current, [kind]: nextGrid };
    });
  }

  function clearDrawing() {
    setDrafts((current) => ({ ...current, [kind]: Array.from({ length: PAINT_GRID_SIZE * PAINT_GRID_SIZE }, () => kind === "background" ? "#14213a" : TRANSPARENT) }));
    setMessage(`${ASSET_LABELS[kind].title} cleared.`);
  }

  function exportDrawing() {
    const output = document.createElement("canvas");
    output.width = 512;
    output.height = 512;
    const context = output.getContext("2d");
    if (!context) return;
    const cell = output.width / PAINT_GRID_SIZE;
    context.clearRect(0, 0, output.width, output.height);
    drafts[kind].forEach((value, index) => {
      if (value === TRANSPARENT) return;
      context.fillStyle = value;
      context.fillRect((index % PAINT_GRID_SIZE) * cell, Math.floor(index / PAINT_GRID_SIZE) * cell, cell, cell);
    });
    output.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `${kind}-pixel-art.png`, { type: "image/png" });
      onUseImage(kind, file);
      setMessage(`${ASSET_LABELS[kind].title} added to the game.`);
    }, "image/png");
  }

  return <div className="paint-layout"><section className="paint-canvas-card"><div className="paint-heading"><div><p className="eyebrow purple">PIXEL PAINTER</p><h3>Draw your {ASSET_LABELS[kind].title.toLowerCase()}</h3></div><select value={kind} onChange={(event) => setKind(event.target.value as AssetKind)} aria-label="Choose image to paint"><option value="hero">Player sprite</option><option value="background">Game background</option><option value="collectible">Score object</option></select></div><canvas ref={canvasRef} width={512} height={512} aria-label={`Pixel painting canvas for ${ASSET_LABELS[kind].title}`} onPointerDown={(event) => { drawing.current = true; event.currentTarget.setPointerCapture(event.pointerId); paint(event); }} onPointerMove={(event) => { if (drawing.current && tool !== "fill") paint(event); }} onPointerUp={() => { drawing.current = false; }} onPointerCancel={() => { drawing.current = false; }} /><p className="paint-message">{message}</p></section><aside className="paint-tools"><div><h3>Tools</h3><div className="paint-tool-row"><button className={classNames(tool === "pencil" && "active")} onClick={() => setTool("pencil")}><Pencil /> Pencil</button><button className={classNames(tool === "eraser" && "active")} onClick={() => setTool("eraser")}><Eraser /> Eraser</button><button className={classNames(tool === "fill" && "active")} onClick={() => setTool("fill")}><PaintBucket /> Fill</button></div></div><div><h3>Colors</h3><div className="paint-colors">{PAINT_COLORS.map((paintColor) => <button key={paintColor} className={classNames(color === paintColor && "active")} aria-label={`Use color ${paintColor}`} style={{ backgroundColor: paintColor }} onClick={() => { setColor(paintColor); setTool("pencil"); }} />)}</div></div><div className="paint-actions"><button className="button secondary" onClick={clearDrawing}><RotateCcw /> Clear</button><button className="button green" onClick={exportDrawing}><Check /> Use in game</button></div><div className="paint-tip"><strong>How it connects</strong><p>Use in game converts the drawing into a PNG and adds it to the student project. It will be included in the final ZIP download.</p></div></aside></div>;
}

function CodePanel({ unit, code, setCode, onRun }: { unit: CourseUnit; code: string; setCode: (code: string) => void; onRun: () => void }) {
  return <div className="code-layout"><section className="code-panel"><header><div><p>YOUR PRIVATE PYTHON FILE</p><strong>main.py</strong></div><span>Starts blank</span></header><textarea aria-label="Python game code" value={code} onChange={(event) => setCode(event.target.value)} spellCheck={false} placeholder="# Watch the smartboard demonstration and type your own code here.\n# Your teacher's example code is not shown in the student account." /><footer><p>Unit {unit.week}: {unit.gameFeature}. Save often and submit this week&apos;s checkpoint when it works.</p><button className="button mint" onClick={onRun}><Play /> Preview game</button></footer></section><aside className="help-stack"><div className="panel"><h3>Before you type</h3><p>Watch the teacher demonstration. Copy carefully, including capitalization, punctuation, and indentation.</p><p>If you get an error, read the last line first and compare that line with the smartboard.</p></div><div className="yellow-card"><strong>Academic integrity</strong><p>Build your own platformer. Ask for help with ideas and errors, but do not copy another student&apos;s file.</p></div></aside></div>;
}

function PlayPanel({ assets, code, grade, onEdit }: { assets: ProjectAssetState; code: string; grade: AutoGrade; onEdit: () => void }) {
  const { speed } = parseGameValues(code);
  const [position, setPosition] = React.useState({ x: 8, y: 68 });
  const [jumping, setJumping] = React.useState(false);
  const move = React.useCallback((dx: number) => {
    setPosition((current) => ({ ...current, x: Math.max(0, Math.min(86, current.x + dx * speed * 0.45)) }));
  }, [speed]);
  const jump = React.useCallback(() => {
    if (jumping) return;
    setJumping(true);
    setPosition((current) => ({ ...current, y: 43 }));
    window.setTimeout(() => {
      setPosition((current) => ({ ...current, y: 68 }));
      setJumping(false);
    }, 450);
  }, [jumping]);
  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => { if (event.key === "ArrowLeft" || event.key === "a") { event.preventDefault(); move(-1); } if (event.key === "ArrowRight" || event.key === "d") { event.preventDefault(); move(1); } if (event.key === "ArrowUp" || event.key === "w" || event.key === " ") { event.preventDefault(); jump(); } };
    window.addEventListener("keydown", handler); return () => window.removeEventListener("keydown", handler);
  }, [jump, move]);
  return <div className="play-layout"><section className="game-stage platform-stage" style={assets.background ? { backgroundImage: `linear-gradient(rgba(10,18,34,.16),rgba(10,18,34,.16)),url(${assets.background.previewUrl})` } : undefined}><div className="game-score"><strong>PLATFORMER VISUAL PREVIEW</strong><span>MOVE + JUMP</span></div><span className="preview-platform ground" /><span className="preview-platform one" /><span className="preview-platform two" /><span className="preview-platform three" /><GameImage asset={assets.hero} fallback="PLAYER" style={{ left: `${position.x}%`, top: `${position.y}%` }} /><GameImage asset={assets.collectible} fallback="★" collectible style={{ left: "82%", top: "27%" }} /><div className="game-controls platform-controls"><button onClick={() => move(-1)}>←</button><button onClick={jump}>JUMP</button><button onClick={() => move(1)}>→</button></div></section><aside className="panel grade-panel"><p className="eyebrow purple">BUILD CHECK</p><div className="score-circle">{grade.score}<small>/100</small></div><div className="grade-list">{grade.checks.map((check) => <div key={check.id} className={check.passed ? "passed" : "missing"}>{check.passed ? <Check /> : <RefreshCw />}<span><strong>{check.label}</strong><small>{check.passed ? `+${check.points} points` : check.feedback}</small></span></div>)}</div><p>This visual preview does not execute Python. Run the real platformer in Python IDLE as your teacher demonstrates.</p><button className="button secondary full" onClick={onEdit}><Code2 /> Edit Python</button></aside></div>;
}

function GameImage({ asset, fallback, collectible, style }: { asset?: ProjectAssetState[AssetKind]; fallback: string; collectible?: boolean; style: React.CSSProperties }) {
  return <div className={classNames("game-object", collectible && "collectible")} style={style}>{asset ? <img src={asset.previewUrl} alt="Game object" /> : <span>{fallback}</span>}</div>;
}

function TeacherDashboard({ viewer }: { viewer: Viewer }) {
  const [projects, setProjects] = React.useState<ProjectRecord[]>([]);
  const [selected, setSelected] = React.useState<ProjectRecord | null>(null);
  const [message, setMessage] = React.useState("Loading student projects…");
  const [feedback, setFeedback] = React.useState("");
  const load = React.useCallback(async () => {
    if (!db) return;
    const projectQuery = viewer.role === "admin" ? collection(db, "projects") : query(collection(db, "projects"), where("className", "==", viewer.className));
    const snapshot = await getDocs(projectQuery);
    const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as ProjectRecord);
    setProjects(rows);
    setSelected((current) => rows.find((row) => row.id === current?.id) || rows[0] || null);
    setFeedback(rows[0]?.teacherFeedback || "");
    setMessage("");
  }, [viewer.className, viewer.role]);
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      void load().catch((problem) => setMessage(problem instanceof Error ? problem.message : "Projects could not be loaded."));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  async function saveFeedback() {
    if (!db || !selected) return;
    await updateDoc(doc(db, "projects", selected.id), { teacherFeedback: feedback, updatedAt: serverTimestamp() });
    setMessage("Feedback saved");
    await load();
  }
  const submitted = projects.filter((project) => project.status === "submitted").length;
  const average = projects.length ? Math.round(projects.reduce((total, project) => total + (project.autoGrade?.score || 0), 0) / projects.length) : 0;
  return <div className="page quest-bg"><div className="page-heading"><div><p className="pill">TEACHER DASHBOARD</p><h1>{viewer.role === "admin" ? "All student projects" : viewer.className}</h1><p>Review code, automatic grades, submissions, and teacher feedback.</p></div><button className="button secondary" onClick={() => void load()}><RefreshCw /> Refresh</button></div><div className="metric-grid"><Metric icon={<Users />} label="Projects" value={projects.length} /><Metric icon={<Send />} label="Submitted" value={submitted} /><Metric icon={<GraduationCap />} label="Average grade" value={`${average}%`} /></div>{message ? <p className="info-message">{message}</p> : null}{projects.length === 0 ? <section className="empty-state"><Users /><h2>No projects yet</h2><p>Student work appears here after the first cloud save.</p></section> : <div className="review-layout"><section className="project-list"><header>Student projects</header>{projects.map((project) => <button className={classNames(selected?.id === project.id && "active")} key={project.id} onClick={() => { setSelected(project); setFeedback(project.teacherFeedback || ""); }}><span><strong>{project.ownerName}</strong><small>{project.ownerEmail}</small></span><b>{project.autoGrade?.score || 0}%</b><em>{project.status}</em></button>)}</section><section className="review-code"><header><div><p>PRIVATE TEACHER VIEW</p><h2>{selected?.ownerName}&apos;s main.py</h2></div><strong>{selected?.autoGrade?.score || 0}%</strong></header><pre><code>{selected?.code}</code></pre><div className="feedback-box"><label htmlFor="teacher-feedback">Teacher feedback</label><textarea id="teacher-feedback" value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="Add feedback for the student…" /><button className="button green" onClick={() => void saveFeedback()}><Save /> Save feedback</button></div></section></div>}</div>;
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return <div className="metric-card"><span>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></div>;
}

function TeacherMediaLibrary({ viewer }: { viewer: Viewer }) {
  const managedClass = viewer.role === "admin" ? "CST 121" : viewer.className;
  const [units, setUnits] = React.useState<CourseUnit[]>(DEFAULT_UNITS);
  const [selectedWeek, setSelectedWeek] = React.useState(1);
  const [settings, setSettings] = React.useState<ClassSettingsRecord>({ className: managedClass, unlockedUnits: [1], resources: {} });
  const [resource, setResource] = React.useState<UnitResource>(() => emptyResource());
  const [message, setMessage] = React.useState("Loading classroom media…");
  const [busy, setBusy] = React.useState(false);
  const selected = units.find((unit) => unit.week === selectedWeek) || units[0];
  const videoEmbed = youtubeEmbedUrl(resource.videoUrl);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([loadCourseUnits(), loadClassSettings(managedClass)]).then(([course, classSettings]) => {
      if (cancelled) return;
      setUnits(course);
      setSettings(classSettings);
      const first = course.find((unit) => unit.week === selectedWeek) || course[0];
      setResource(classSettings.resources?.[first.id] || emptyResource());
      setMessage("Choose a unit, paste your links, and save. Students will see them inside that lesson.");
    }).catch(() => !cancelled && setMessage("The media area could not load."));
    return () => { cancelled = true; };
  }, [managedClass, selectedWeek]);

  function chooseUnit(week: number) {
    setSelectedWeek(week);
    const unit = units.find((item) => item.week === week);
    setResource(unit ? settings.resources?.[unit.id] || emptyResource() : emptyResource());
  }

  async function saveMedia(event: React.FormEvent) {
    event.preventDefault();
    if (!db || !selected) return;
    setBusy(true);
    try {
      for (const [label, value] of [["YouTube", resource.videoUrl], ["slides", resource.slidesUrl]] as const) {
        if (value.trim()) {
          try { new URL(value); } catch { throw new Error(`The ${label} link needs to start with https://`); }
        }
      }
      const next: ClassSettingsRecord = {
        className: managedClass,
        unlockedUnits: settings.unlockedUnits,
        resources: { ...settings.resources, [selected.id]: resource },
      };
      await setDoc(doc(db, "classSettings", classIdFor(managedClass)), { ...next, updatedAt: serverTimestamp() }, { merge: true });
      setSettings(next);
      setMessage(`Unit ${selected.week} video and slide links are now saved for students.`);
    } catch (problem) {
      setMessage(problem instanceof Error ? problem.message : "The media links could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  return <div className="page quest-bg"><div className="page-heading"><div><p className="pill">TEACHER MEDIA LIBRARY</p><h1>YouTube videos and lesson slides</h1><p>Attach your own video or slides to any weekly unit. Students open them from the lesson page.</p></div><span className="role-badge"><Video /> {managedClass}</span></div>{message ? <p className="info-message course-message">{message}</p> : null}<div className="media-layout"><aside className="unit-picker"><header><strong>Choose a lesson</strong><small>15 weekly units</small></header>{units.map((unit) => <button className={classNames(selected.week === unit.week && "active")} key={unit.id} onClick={() => chooseUnit(unit.week)}><span>{unit.week}</span><div><small>UNIT {unit.week}</small><strong>{unit.title}</strong></div></button>)}</aside><form className="panel media-editor" onSubmit={saveMedia}><header><p className="eyebrow purple">UNIT {selected.week} MEDIA</p><h2>{selected.title}</h2><p>Paste the Share link from YouTube. You can replace or remove it at any time.</p></header><label><span><Video /> YouTube lesson link</span><input type="url" value={resource.videoUrl} onChange={(event) => setResource((current) => ({ ...current, videoUrl: event.target.value }))} placeholder="https://youtu.be/your-video" /></label>{videoEmbed ? <div className="media-preview"><iframe src={videoEmbed} title="Teacher video preview" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div> : <div className="media-placeholder"><Video /><strong>Your YouTube preview will appear here</strong><span>Students will see the same video in their lesson.</span></div>}<label><span><Link /> Slides or lesson webpage</span><input type="url" value={resource.slidesUrl} onChange={(event) => setResource((current) => ({ ...current, slidesUrl: event.target.value }))} placeholder="https://docs.google.com/presentation/..." /></label><label><span><Pencil /> Private teacher note</span><textarea value={resource.teacherNote} onChange={(event) => setResource((current) => ({ ...current, teacherNote: event.target.value }))} placeholder="Materials, reminders, accommodations, or discussion prompts…" /></label><footer><span>Students see the video and slides. They never see the private note.</span><button className="button primary" type="submit" disabled={busy}><Save /> Save Unit {selected.week} media</button></footer></form></div></div>;
}

function TeacherGuide({ viewer }: { viewer: Viewer }) {
  const managedClass = viewer.role === "admin" ? "CST 121" : viewer.className;
  const [units, setUnits] = React.useState<CourseUnit[]>(DEFAULT_UNITS);
  const [guides, setGuides] = React.useState<TeacherGuideRecord[]>([]);
  const [selectedWeek, setSelectedWeek] = React.useState(1);
  const [settings, setSettings] = React.useState<ClassSettingsRecord>({ className: managedClass, unlockedUnits: [1], resources: {} });
  const [resource, setResource] = React.useState<UnitResource>(() => emptyResource());
  const [message, setMessage] = React.useState("Loading teacher guide…");
  const [busy, setBusy] = React.useState(false);
  const selected = units.find((unit) => unit.week === selectedWeek) || units[0];
  const selectedGuide = guides.find((guide) => guide.id === selected.id) || emptyTeacherGuide(selected);

  const load = React.useCallback(async () => {
    const [course, teacherGuides, classSettings] = await Promise.all([loadCourseUnits(), loadTeacherGuides(), loadClassSettings(managedClass)]);
    setUnits(course);
    setGuides(teacherGuides);
    setSettings(classSettings);
    setResource(classSettings.resources?.[selectedWeek ? `unit-${String(selectedWeek).padStart(2, "0")}` : course[0].id] || emptyResource());
    setMessage("");
  }, [managedClass, selectedWeek]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => { void load().catch(() => setMessage("The teacher guide could not be loaded.")); }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  function chooseUnit(week: number) {
    setSelectedWeek(week);
    const unit = units.find((item) => item.week === week);
    setResource(unit ? settings.resources?.[unit.id] || emptyResource() : emptyResource());
  }

  async function saveClassSettings(nextUnlocked = settings.unlockedUnits, nextResource = resource) {
    if (!db || !selected) return;
    setBusy(true);
    try {
      const next: ClassSettingsRecord = {
        className: managedClass,
        unlockedUnits: Array.from(new Set([1, ...nextUnlocked])).sort((a, b) => a - b),
        resources: { ...settings.resources, [selected.id]: nextResource },
      };
      await setDoc(doc(db, "classSettings", classIdFor(managedClass)), { ...next, updatedAt: serverTimestamp() }, { merge: true });
      setSettings(next);
      setMessage(`Unit ${selected.week} classroom settings saved.`);
    } catch (problem) {
      setMessage(problem instanceof Error ? problem.message : "Classroom settings could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleUnlock() {
    const isUnlocked = settings.unlockedUnits.includes(selected.week);
    const nextUnlocked = isUnlocked && selected.week !== 1
      ? settings.unlockedUnits.filter((week) => week !== selected.week)
      : [...settings.unlockedUnits, selected.week];
    await saveClassSettings(nextUnlocked);
  }

  return <div className="page quest-bg"><div className="page-heading"><div><p className="pill">PRIVATE TEACHER AREA</p><h1>Platformer teacher&apos;s guide</h1><p>Project the code, lead the lesson, unlock units, and add class resources. Students cannot see this page.</p></div><span className="role-badge"><ShieldCheck /> {viewer.role === "admin" ? "Administrator view" : `${viewer.className} teacher`}</span></div>{message ? <p className="info-message course-message">{message}</p> : null}<div className="guide-layout"><aside className="unit-picker"><header><strong>15 weekly units</strong><small>{managedClass}</small></header>{units.map((unit) => <button className={classNames(selected.week === unit.week && "active")} key={unit.id} onClick={() => chooseUnit(unit.week)}><span>{settings.unlockedUnits.includes(unit.week) ? <Unlock /> : <LockKeyhole />}</span><div><small>UNIT {unit.week}</small><strong>{unit.title}</strong></div></button>)}</aside><section className="guide-content"><div className="guide-heading"><div><p className="eyebrow purple">UNIT {selected.week} · TEACHER GUIDE</p><h2>{selected.title}</h2><p>{selected.pythonFocus} → {selected.gameFeature}</p></div><button className={classNames("button", settings.unlockedUnits.includes(selected.week) ? "secondary" : "green")} disabled={busy || selected.week === 1} onClick={() => void toggleUnlock()}>{settings.unlockedUnits.includes(selected.week) ? <LockKeyhole /> : <Unlock />}{selected.week === 1 ? "Unit 1 always open" : settings.unlockedUnits.includes(selected.week) ? "Lock for students" : "Unlock for students"}</button></div>{selectedGuide.demoCode ? <div className="guide-columns"><section className="panel"><h3>Lesson plan</h3><ol className="number-list">{selectedGuide.teacherPlan.map((step) => <li key={step}>{step}</li>)}</ol><h3>Student checkpoint</h3><p>{selected.assignment}</p><h3>Quick rubric</h3><ul className="check-list">{selectedGuide.rubric.map((item) => <li key={item}><ClipboardCheck />{item}</li>)}</ul></section><section className="teacher-code"><header><div><p>SMARTBOARD CODE</p><strong>Teacher view only</strong></div><span>Copy-along example</span></header><pre><code>{selectedGuide.demoCode}</code></pre><footer>Show one section at a time. Pause so students can type, run, and debug before continuing.</footer></section></div> : <section className="panel"><h3>Teacher guide not published yet</h3><p>The administrator can add the lesson plan, rubric, and smartboard code in Course Builder.</p></section>}<section className="panel resource-editor"><div><h3>Slides, video, and class note</h3><p>Add optional resources for this unit. Students see the slide and video links in their lesson.</p></div><label>Slides URL<input type="url" value={resource.slidesUrl} onChange={(event) => setResource((current) => ({ ...current, slidesUrl: event.target.value }))} placeholder="https://docs.google.com/presentation/..." /></label><label>Video URL<input type="url" value={resource.videoUrl} onChange={(event) => setResource((current) => ({ ...current, videoUrl: event.target.value }))} placeholder="https://youtu.be/..." /></label><label>Private teacher note<textarea value={resource.teacherNote} onChange={(event) => setResource((current) => ({ ...current, teacherNote: event.target.value }))} placeholder="Materials, accommodations, reminders, or class-specific notes…" /></label><button className="button primary" disabled={busy} onClick={() => void saveClassSettings()}><Save /> Save resources</button></section></section></div></div>;
}

function CourseBuilder() {
  const [units, setUnits] = React.useState<CourseUnit[]>(DEFAULT_UNITS);
  const [selectedId, setSelectedId] = React.useState(DEFAULT_UNITS[0].id);
  const [draft, setDraft] = React.useState<CourseUnit>({ ...DEFAULT_UNITS[0] });
  const [guides, setGuides] = React.useState<TeacherGuideRecord[]>([]);
  const [guideDraft, setGuideDraft] = React.useState<TeacherGuideRecord>(() => emptyTeacherGuide(DEFAULT_UNITS[0]));
  const [message, setMessage] = React.useState("Loading the course builder…");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    const [course, teacherGuides] = await Promise.all([loadCourseUnits(), loadTeacherGuides()]);
    setUnits(course);
    setGuides(teacherGuides);
    const selected = course.find((unit) => unit.id === selectedId) || course[0];
    setSelectedId(selected.id);
    setDraft({ ...selected });
    setGuideDraft({ ...(teacherGuides.find((guide) => guide.id === selected.id) || emptyTeacherGuide(selected)) });
    setMessage("The 15-unit starter curriculum is ready. Edit any field and save it live.");
  }, [selectedId]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => { void load().catch(() => setMessage("The course builder could not load saved edits.")); }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  function chooseUnit(id: string) {
    const unit = units.find((item) => item.id === id);
    if (!unit) return;
    setSelectedId(id);
    setDraft({ ...unit });
    setGuideDraft({ ...(guides.find((guide) => guide.id === id) || emptyTeacherGuide(unit)) });
  }

  async function saveUnit(event: React.FormEvent) {
    event.preventDefault();
    if (!db) return;
    setBusy(true);
    try {
      await Promise.all([
        setDoc(doc(db, "courseUnits", draft.id), { ...draft, updatedAt: serverTimestamp() }, { merge: true }),
        setDoc(doc(db, "teacherGuides", guideDraft.id), { ...guideDraft, updatedAt: serverTimestamp() }, { merge: true }),
      ]);
      setUnits((current) => current.map((unit) => unit.id === draft.id ? draft : unit));
      setGuides((current) => [...current.filter((guide) => guide.id !== guideDraft.id), guideDraft]);
      setMessage(`Unit ${draft.week} is saved. Students and teachers will see the update.`);
    } catch (problem) {
      setMessage(problem instanceof Error ? problem.message : "The unit could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  function setText<K extends keyof CourseUnit>(key: K, value: CourseUnit[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function setGuideText<K extends keyof TeacherGuideRecord>(key: K, value: TeacherGuideRecord[K]) {
    setGuideDraft((current) => ({ ...current, [key]: value }));
  }

  function lines(value: string) {
    return value.split("\n").map((item) => item.trim()).filter(Boolean);
  }

  return <div className="page quest-bg"><div className="page-heading"><div><p className="pill">ADMINISTRATOR WEBSITE BUILDER</p><h1>Edit the course without changing code</h1><p>Change student lessons, teacher guides, assignments, rubrics, and smartboard code.</p></div><span className="role-badge admin"><Settings /> Administrator only</span></div>{message ? <p className="info-message course-message">{message}</p> : null}<div className="builder-layout"><aside className="unit-picker builder-picker"><header><strong>Course pages</strong><small>15 units</small></header>{units.map((unit) => <button className={classNames(selectedId === unit.id && "active")} key={unit.id} onClick={() => chooseUnit(unit.id)}><span>{unit.week}</span><div><small>{unit.published ? "PUBLISHED" : "HIDDEN"}</small><strong>{unit.title}</strong></div></button>)}</aside><form className="builder-form" onSubmit={saveUnit}><header><div><p className="eyebrow purple">EDIT UNIT {draft.week}</p><h2>{draft.title}</h2></div><label className="publish-switch"><input type="checkbox" checked={draft.published} onChange={(event) => setText("published", event.target.checked)} /> Published</label></header><div className="builder-grid"><BuilderField label="Unit title"><input value={draft.title} onChange={(event) => setText("title", event.target.value)} /></BuilderField><BuilderField label="Python focus"><input value={draft.pythonFocus} onChange={(event) => setText("pythonFocus", event.target.value)} /></BuilderField><BuilderField label="Platform-game feature"><input value={draft.gameFeature} onChange={(event) => setText("gameFeature", event.target.value)} /></BuilderField><BuilderField label="Student summary"><textarea value={draft.summary} onChange={(event) => setText("summary", event.target.value)} /></BuilderField><BuilderField label="Learning objectives (one per line)"><textarea value={draft.objectives.join("\n")} onChange={(event) => setText("objectives", lines(event.target.value))} /></BuilderField><BuilderField label="Vocabulary (comma separated)"><input value={draft.vocabulary.join(", ")} onChange={(event) => setText("vocabulary", event.target.value.split(",").map((item) => item.trim()).filter(Boolean))} /></BuilderField><BuilderField label="Student build steps (one per line)" wide><textarea value={draft.studentSteps.join("\n")} onChange={(event) => setText("studentSteps", lines(event.target.value))} /></BuilderField><BuilderField label="Weekly assignment" wide><textarea value={draft.assignment} onChange={(event) => setText("assignment", event.target.value)} /></BuilderField><BuilderField label="Teacher lesson plan (one step per line)" wide><textarea value={guideDraft.teacherPlan.join("\n")} onChange={(event) => setGuideText("teacherPlan", lines(event.target.value))} /></BuilderField><BuilderField label="Smartboard demonstration code" wide><textarea className="builder-code" value={guideDraft.demoCode} onChange={(event) => setGuideText("demoCode", event.target.value)} spellCheck={false} /></BuilderField><BuilderField label="Rubric items (one per line)" wide><textarea value={guideDraft.rubric.join("\n")} onChange={(event) => setGuideText("rubric", lines(event.target.value))} /></BuilderField></div><footer><span>Only the administrator can save course-wide changes.</span><button className="button primary" type="submit" disabled={busy}><Save /> Save Unit {draft.week}</button></footer></form></div></div>;
}

function BuilderField({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={classNames("builder-field", wide && "wide")}><span>{label}</span>{children}</label>;
}

function AccountManagement() {
  const [invites, setInvites] = React.useState<InviteRecord[]>([]);
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [role, setRole] = React.useState<"student" | "teacher">("student");
  const [className, setClassName] = React.useState("CST 121");
  const [message, setMessage] = React.useState("");
  const load = React.useCallback(async () => {
    if (!db) return;
    const snapshot = await getDocs(collection(db, "invites"));
    setInvites(snapshot.docs.map((item) => item.data() as InviteRecord));
  }, []);
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      void load().catch(() => setMessage("Approved accounts could not be loaded."));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  async function approve(event: React.FormEvent) {
    event.preventDefault();
    if (!db) return;
    const normalizedEmail = email.trim().toLowerCase();
    const invite: InviteRecord = { email: normalizedEmail, displayName: displayName.trim() || normalizedEmail, role, className: className.trim(), active: true };
    await setDoc(doc(db, "invites", normalizedEmail), { ...invite, updatedAt: serverTimestamp() });
    setEmail(""); setDisplayName(""); setMessage(`${normalizedEmail} is approved.`); await load();
  }
  return <div className="page quest-bg"><div className="page-heading"><div><p className="pill">ADMINISTRATOR TOOLS</p><h1>Google account access</h1><p>Approve teachers and students before they enter the Game Lab.</p></div></div><div className="account-layout"><form className="panel account-form" onSubmit={approve}><h2><UserPlus /> Approve an account</h2><label>Google email<input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="student@school.org" /></label><label>Display name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Student name" /></label><label>Role<select value={role} onChange={(event) => setRole(event.target.value as "student" | "teacher")}><option value="student">Student</option><option value="teacher">Teacher</option></select></label><label>Class<input required value={className} onChange={(event) => setClassName(event.target.value)} /></label><button className="button primary full" type="submit"><UserPlus /> Approve Google account</button>{message ? <p className="info-message">{message}</p> : null}</form><section className="panel account-table"><header><h2>Approved accounts</h2><p>Students see their work. Teachers see their assigned class.</p></header><div className="table-scroll"><table><thead><tr><th>Name</th><th>Google email</th><th>Role</th><th>Class</th></tr></thead><tbody>{invites.map((invite) => <tr key={invite.email}><td><strong>{invite.displayName}</strong></td><td>{invite.email}</td><td>{invite.role}</td><td>{invite.className}</td></tr>)}</tbody></table></div></section></div></div>;
}
